import { spawn } from "node:child_process";
import { LocalCliError } from "./errors.js";
import type { RepoProbe } from "./github-app-repo.js";

export interface ExecResult { stdout: string; stderr: string; exitCode: number }
export type GhExec = (cmd: string, args: string[]) => Promise<ExecResult>;

/**
 * Default exec — wraps Node's spawn. Tests inject a mocked GhExec.
 */
export const defaultGhExec: GhExec = (cmd, args) =>
  new Promise((resolve) => {
    const child = spawn(cmd, args, { stdio: ["ignore", "pipe", "pipe"] });
    let stdout = "";
    let stderr = "";
    let settled = false;
    const done = (r: ExecResult): void => { if (!settled) { settled = true; resolve(r); } };
    child.stdout.on("data", (d: Buffer) => { stdout += d.toString(); });
    child.stderr.on("data", (d: Buffer) => { stderr += d.toString(); });
    child.on("close", (code) => { done({ stdout, stderr, exitCode: code ?? -1 }); });
    // A missing binary (spawn ENOENT) emits an async 'error' event. WITHOUT this
    // handler it becomes an unhandled event that CRASHES the process — which is
    // exactly what killed the updater job's converge when `gh` was absent from
    // the image (the getGhToken best-effort path could never catch it). Resolve
    // non-zero so getGhToken throws a normal LocalCliError its callers already
    // treat as "no token → fetch anonymously".
    child.on("error", (err: Error) => { done({ stdout, stderr: stderr || err.message, exitCode: -1 }); });
  });

/** True iff `gh auth status` exits 0. */
export async function isGhAuthed(exec: GhExec = defaultGhExec): Promise<boolean> {
  const r = await exec("gh", ["auth", "status"]);
  return r.exitCode === 0;
}

/**
 * Run `gh auth login --device --scopes "repo,read:org"` interactively.
 *
 * NB: device-flow prompts the operator with a browser URL + one-time code.
 * The default exec inherits stdin/stdout/stderr from the parent process,
 * so the prompt is visible to the operator. (For tests, use a custom exec
 * that simulates a successful login.)
 */
export async function ghAuthLoginDevice(exec: GhExec = defaultGhExec): Promise<void> {
  const r = await exec("gh", [
    "auth", "login", "--device", "--scopes", "repo,read:org",
  ]);
  if (r.exitCode !== 0) {
    throw new LocalCliError({
      code: "GH_AUTH_FAILED",
      message: `gh auth login failed: ${r.stderr || r.stdout}`,
      hint: "Re-run interactively: gh auth login --device --scopes 'repo,read:org'",
    });
  }
}

/** The operator's GitHub OAuth token (for authenticated git pushes on the local path). */
export async function getGhToken(exec: GhExec = defaultGhExec): Promise<string> {
  const r = await exec("gh", ["auth", "token"]);
  const tok = r.stdout.trim();
  if (r.exitCode !== 0 || !tok) {
    throw new LocalCliError({
      code: "GH_TOKEN_FAILED",
      message: `gh auth token failed (exit ${r.exitCode.toString()}): ${(r.stderr || r.stdout).slice(0, 200)}`,
      hint: "Run `gh auth login` to authenticate the GitHub CLI.",
    });
  }
  return tok;
}

/** Get a repo's numeric GitHub id (used for the App install URL). */
export async function getRepoId(
  owner: string,
  repo: string,
  exec: GhExec = defaultGhExec,
): Promise<number> {
  const r = await exec("gh", ["api", `/repos/${owner}/${repo}`, "--jq", ".id"]);
  if (r.exitCode !== 0) {
    throw new LocalCliError({
      code: "GH_REPO_ID_FAILED",
      message: `gh api /repos/${owner}/${repo}: ${r.stderr || r.stdout}`,
    });
  }
  const id = Number(r.stdout.trim());
  if (!Number.isInteger(id) || id <= 0) {
    throw new LocalCliError({
      code: "GH_REPO_ID_INVALID",
      message: `Expected numeric repo id, got '${r.stdout.trim()}'`,
    });
  }
  return id;
}

/** Build the per-repo App install URL (`?repository_ids=<id>` preselects). */
export function installUrl(slug: string, repoId: number): string {
  return `https://github.com/apps/${slug}/installations/new?repository_ids=${repoId.toString()}`;
}

/**
 * `gh repo create` from a local source dir, push to main.
 *
 * Precondition: source dir must already be a git repo with at least one
 * commit (`git init && git add . && git commit`). `gh repo create
 * --source --push` validates this and bails before creating the remote
 * repo otherwise.
 *
 * Throws on non-zero exit.
 */
export async function createRepo(args: {
  owner: string;
  name: string;
  private?: boolean;
  source: string;
  exec?: GhExec;
}): Promise<void> {
  const exec = args.exec ?? defaultGhExec;
  const visibility = args.private ? "--private" : "--public";
  const r = await exec("gh", [
    "repo", "create", `${args.owner}/${args.name}`,
    visibility, "--source", args.source, "--remote", "origin", "--push",
  ]);
  if (r.exitCode !== 0) {
    throw new LocalCliError({
      code: "GH_REPO_CREATE_FAILED",
      message: `gh repo create: ${r.stderr || r.stdout}`,
    });
  }
}

/**
 * Poll for App installation on a repo every `intervalMs` until detected or
 * the timeout fires. Returns the installation id.
 *
 * Auth note: `GET /repos/{owner}/{repo}/installation` REQUIRES an App JWT —
 * the operator's user PAT (what `gh api` sends) returns
 * `401 "JSON web token could not be decoded"`. So callers MUST supply a
 * `probe` that signs an App JWT and queries the endpoint themselves. The
 * legacy gh-CLI path is only used when no probe is given; it works only on
 * a small set of public/no-auth endpoints and is preserved for back-compat.
 */
export async function pollForInstallation(args: {
  owner: string;
  repo: string;
  intervalMs?: number;
  timeoutMs?: number;
  exec?: GhExec;
  /** Custom probe; returns the installation id when detected, or null/undefined to keep waiting. */
  probe?: () => Promise<string | null | undefined>;
  onTick?: (attempt: number) => void;
}): Promise<string> {
  const exec = args.exec ?? defaultGhExec;
  const intervalMs = args.intervalMs ?? 2000;
  const timeoutMs = args.timeoutMs ?? 5 * 60 * 1000;
  const start = Date.now();
  let attempt = 0;
  for (;;) {
    attempt++;
    args.onTick?.(attempt);
    let id: string | null | undefined = null;
    if (args.probe) {
      try { id = await args.probe(); } catch { /* keep polling */ }
    } else {
      // Legacy gh-CLI path — only works if the endpoint accepts user PAT.
      const r = await exec("gh", [
        "api", `/repos/${args.owner}/${args.repo}/installation`, "--jq", ".id",
      ]);
      if (r.exitCode === 0) {
        const v = r.stdout.trim();
        if (v && v !== "null") id = v;
      }
    }
    if (id) return id;
    if (Date.now() - start >= timeoutMs) {
      throw new LocalCliError({
        code: "GH_INSTALL_POLL_TIMEOUT",
        message: `Timed out after ${String(timeoutMs / 1000)}s waiting for the App to be installed on ${args.owner}/${args.repo}.`,
        hint: `Did you click 'Install' in the browser? Open the URL again if needed.`,
      });
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * Probe whether the GitHub App is installed on a given repo. Uses the
 * App-JWT identity (gh user-PAT is rejected on this endpoint).
 *
 * Returns the installation id when present (HTTP 200), null when absent
 * (HTTP 404). Throws on any other non-2xx so the caller doesn't silently
 * treat e.g. a 500 as "not installed".
 *
 * Lifted from the inline closures in brain/link.ts and brain/create.ts
 * so it can be reused as both: (a) the pre-tryOpenUrl probe
 * (skip browser open when already installed), and (b) the polling probe
 * for pollForInstallation.
 */
export async function probeAppInstallation(args: {
  owner: string;
  repo: string;
  jwt: string;
}): Promise<string | null> {
  const res = await fetch(`https://api.github.com/repos/${args.owner}/${args.repo}/installation`, {
    headers: {
      Authorization: `Bearer ${args.jwt}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
    },
  });
  if (res.ok) {
    const j = (await res.json()) as { id?: number | string };
    return j.id !== undefined ? String(j.id) : null;
  }
  if (res.status === 404) return null;
  const body = await res.text();
  throw new Error(`probeAppInstallation: HTTP ${res.status.toString()} from /repos/${args.owner}/${args.repo}/installation\n${body.slice(0, 300)}`);
}

/**
 * Try to open a URL in the operator's default browser. Best-effort —
 * silently no-ops on failure so the CLI still prints the URL.
 */
export async function tryOpenUrl(url: string, exec: GhExec = defaultGhExec): Promise<void> {
  const cmd = process.platform === "darwin" ? "open"
            : process.platform === "win32" ? "start"
            : "xdg-open";
  try { await exec(cmd, [url]); } catch { /* best-effort */ }
}

/**
 * A RepoProbe backed by the `gh` CLI (the local path — uses the operator's PAT).
 * repoStatus parses `gh api -i` header output; getFile reads a file's raw
 * contents; isEmpty treats a 404 on the repo-root contents as "no commits".
 */
export function ghRepoProbe(args: { owner: string; repo: string; exec?: GhExec }): RepoProbe {
  const exec = args.exec ?? defaultGhExec;
  const slug = `${args.owner}/${args.repo}`;
  return {
    repoStatus: async () => {
      const r = await exec("gh", ["api", "-i", `/repos/${slug}`]);
      const combined = `${r.stdout}\n${r.stderr}`;
      const header = /^HTTP\/[\d.]+\s+(\d{3})/m.exec(combined);
      if (header) return Number(header[1]);
      const ghErr = /\(HTTP (\d{3})\)/.exec(combined); // gh's "Not Found (HTTP 404)" form
      if (ghErr) return Number(ghErr[1]);
      if (r.exitCode === 0) return 200;
      // Ambiguous failure (network/auth/rate-limit) — do NOT assume the repo is absent,
      // or a transient error would trigger a duplicate repo create.
      throw new LocalCliError({
        code: "GH_REPO_PROBE_FAILED",
        message: `gh api /repos/${slug} failed (exit ${r.exitCode.toString()}): ${(r.stderr || r.stdout).slice(0, 200)}`,
      });
    },
    getFile: async (path) => {
      // -H Accept: raw returns the file body directly; non-zero exit → treat as absent.
      const r = await exec("gh", ["api", "-H", "Accept: application/vnd.github.raw+json", `/repos/${slug}/contents/${path}`]);
      return r.exitCode === 0 ? r.stdout : null;
    },
    isEmpty: async () => {
      const r = await exec("gh", ["api", `/repos/${slug}/contents`]);
      return r.exitCode !== 0; // 404 on an empty repo
    },
  };
}
